"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { usePathname } from "@frontend/lib/navigation";
import { clientApi } from "@frontend/lib/api";
import { Avatar, cx } from "@frontend/components/ui";
import { creatorAvatarUrl } from "@frontend/lib/avatar";
import { cacheClear } from "@frontend/lib/data-cache";
import { useRefresh } from "@frontend/lib/refresh";
import { useViewer } from "@frontend/lib/viewer";
import type { AccessibleAccount } from "@creators/backend/lib/access/members";
import { ROLE_LABELS, type MemberRole } from "@creators/backend/lib/access/roles";

type Entry = {
  /** Null is the viewer's own identity, for someone who owns no creator account. */
  creatorId: string | null;
  label: string;
  role: MemberRole | null;
  hint: string;
  avatarUrl: string | null;
};

/**
 * Picks which creator account the session is looking at. Every scoped read on the
 * server keys off the session's active account, so a switch changes the whole app
 * at once — soft-reload viewer + caches instead of a full page navigation.
 */
export function AccountSwitcher({
  accounts,
  activeCreatorId,
  personalLabel,
}: {
  accounts: AccessibleAccount[];
  activeCreatorId: string | null;
  personalLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const navigate = useNavigate();
  const { reload } = useViewer();
  const { refresh } = useRefresh();

  useEffect(() => {
    if (!open || pendingId) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, pendingId]);

  const owned = accounts.find((account) => account.own);

  const entries: Entry[] = [
    ...(owned
      ? []
      : [
          {
            creatorId: null,
            label: personalLabel,
            role: null,
            hint: "Your own collabs",
            avatarUrl: null,
          },
        ]),
    ...accounts.map((account) => ({
      creatorId: account.creatorId,
      label: account.displayName,
      role: account.role,
      hint: account.own
        ? "Your account"
        : account.handle
          ? `@${account.handle}`
          : "Shared with you",
      avatarUrl: creatorAvatarUrl(account.creatorId, account.avatarKey),
    })),
  ];

  // Nothing to switch between: the nav stays as it was before team access existed.
  if (entries.length < 2) return null;

  const active = entries.find((entry) => entry.creatorId === activeCreatorId) ?? entries[0]!;
  const delegated = Boolean(active.role && active.role !== "owner");
  const switching = pendingId !== null;

  const select = async (entry: Entry) => {
    if (entry.creatorId === activeCreatorId || switching) {
      setOpen(false);
      return;
    }

    const key = entry.creatorId ?? "personal";
    setPendingId(key);
    setPendingLabel(entry.label);
    setError(null);
    setOpen(false);

    const next = pathname.startsWith("/collabs/") ? "/collabs" : pathname;
    const formData = new FormData();
    formData.set("creatorId", entry.creatorId ?? "personal");
    formData.set("next", next);

    try {
      // Soft switch — avoid full page reload (that felt broken with no feedback).
      const result = await clientApi<{ ok?: boolean; error?: string; redirectTo?: string }>(
        "/api/accounts/switch",
        { method: "POST", body: formData },
      );
      if (result.error) {
        setError(result.error);
        setPendingId(null);
        setPendingLabel(null);
        setOpen(true);
        return;
      }

      cacheClear();
      await reload();
      refresh();

      const dest = result.redirectTo ?? next;
      if (dest !== pathname) navigate(dest, { replace: true });
    } catch {
      setError("Could not switch accounts. Try again.");
      setOpen(true);
    } finally {
      setPendingId(null);
      setPendingLabel(null);
    }
  };

  return (
    <div ref={rootRef} className="relative min-w-0 max-w-[9.5rem] sm:max-w-[14rem]">
      <button
        type="button"
        onClick={() => {
          if (switching) return;
          setOpen((value) => !value);
        }}
        disabled={switching}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-busy={switching}
        aria-label={`Current account: ${active.label}. Switch account`}
        className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-line bg-surface py-2 pr-2 pl-2.5 text-sm transition hover:bg-canvas disabled:opacity-70"
      >
        <Avatar name={active.label} url={active.avatarUrl} size="sm" className="!size-5" />
        <span className="min-w-0 truncate font-medium text-ink">{active.label}</span>
        {delegated ? (
          <span className="hidden shrink-0 rounded-md bg-canvas px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-ink-muted uppercase ring-1 ring-line sm:inline">
            {ROLE_LABELS[active.role!]}
          </span>
        ) : null}
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={cx("size-4 shrink-0 text-ink-subtle transition", open && "rotate-180")}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-2 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-10px_rgba(15,18,24,0.18)]"
        >
          <p className="px-3 py-2 text-[10px] font-semibold tracking-wide text-ink-subtle uppercase">
            Switch account
          </p>

          {entries.map((entry) => {
            const key = entry.creatorId ?? "personal";
            const isActive = entry.creatorId === activeCreatorId;

            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                onClick={() => void select(entry)}
                disabled={switching}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-canvas disabled:opacity-60"
              >
                <Avatar name={entry.label} url={entry.avatarUrl} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">{entry.label}</span>
                  <span className="block truncate text-xs text-ink-subtle">
                    {entry.role && entry.role !== "owner"
                      ? `${ROLE_LABELS[entry.role]} · ${entry.hint}`
                      : entry.hint}
                  </span>
                </span>

                {isActive ? (
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-4 shrink-0 text-money-600"
                    aria-label="Current account"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : null}
              </button>
            );
          })}

          {error ? (
            <p className="border-t border-line/70 px-3 py-2 text-xs text-alert-600">{error}</p>
          ) : null}
        </div>
      ) : null}

      {switching && pendingLabel
        ? createPortal(
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-canvas/70 backdrop-blur-[2px]"
              role="status"
              aria-live="polite"
              aria-label={`Switching to ${pendingLabel}`}
            >
              <div className="flex min-w-[14rem] flex-col items-center gap-3 rounded-2xl border border-line bg-surface px-8 py-7 shadow-[0_16px_40px_-16px_rgba(15,18,24,0.35)]">
                <Spinner />
                <p className="text-sm font-semibold text-ink">Switching account</p>
                <p className="max-w-[14rem] truncate text-center text-xs text-ink-muted">
                  {pendingLabel}
                </p>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="size-7 animate-spin text-ink"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-20"
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
